/** @file Hook: the curve tracks to show, from the document, the selection, the playhead animation. */
import type { Doc } from '@pose2d/format/types';
import { useMemo } from 'react';
import { useStore } from 'zustand';
import { docStore } from '../../stores/docStore';
import { playheadStore } from '../../stores/playheadStore';
import { selectionStore } from '../../stores/selectionStore';
import type { KeyRef } from '../../stores/types';
import { buildCurveTrack } from './buildCurveTrack';
import type { CurvePorts } from './curvePorts';
import type { CurveRef, CurveTrack } from './curveTypes';
import { curveViewStore } from './curveViewStore';
import { shownCurveRefs } from './shownCurveRefs';

/** What the Curves tab shows; `refs[i]` is the timeline of `tracks[i]`. */
export interface CurveModel {
  doc: Doc | null;
  refs: CurveRef[];
  tracks: CurveTrack[];
  keys: KeyRef[];
}

/** Recomputed when the document, the selection, the animation or the pins change (never per frame). */
export function useCurveModel(ports: CurvePorts): CurveModel {
  const slice = useStore(docStore, (s) => s.slice);
  const selection = useStore(selectionStore);
  const animation = useStore(playheadStore, (s) => s.animation);
  const pinned = useStore(curveViewStore, (s) => s.pinned);
  const ownKeys = useStore(curveViewStore, (s) => s.ownKeys);
  return useMemo(() => {
    const doc = slice === null ? null : ports.doc();
    if (doc === null) return { doc, refs: [], tracks: [], keys: selection.keys };
    const refs = shownCurveRefs(doc, animation, selection, ownKeys === selection.keys ? pinned : []);
    const tracks = refs.flatMap((ref) => buildCurveTrack(doc, ref) ?? []);
    return { doc, refs, tracks, keys: selection.keys };
  }, [ports, slice, selection, animation, pinned, ownKeys]);
}
